import axios from 'axios';

const statusAllowsGcsResumableChunkResponse = (status: number) =>
  (status >= 200 && status < 300) || status === 308;

export function isGcsResumableUploadUrl(uploadUrl: string): boolean {
  try {
    const { searchParams } = new URL(uploadUrl);
    return (
      searchParams.get('uploadType')?.toLowerCase() === 'resumable'
    );
  } catch {
    return false;
  }
}

export async function putGcsResumableUploadInChunks(
  uploadUrl: string,
  file: File,
  signal: AbortSignal,
  chunkSizeBytes: number,
  onProgress: (percent: number) => void,
  startOffset = 0,
): Promise<void> {
  const contentType = file.type || 'application/octet-stream';

  if (file.size === 0) {
    await axios.put(uploadUrl, new Blob([]), {
      headers: {
        'Content-Type': contentType,
        'Content-Range': 'bytes */0',
      },
      signal,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      validateStatus: statusAllowsGcsResumableChunkResponse,
    });
    onProgress(100);
    return;
  }

  let offset = startOffset;
  while (offset < file.size) {
    if (signal.aborted) {
      throw new DOMException('Upload aborted', 'AbortError');
    }

    const end = Math.min(offset + chunkSizeBytes, file.size) - 1;
    const chunk = file.slice(offset, end + 1);
    const contentRange = `bytes ${offset}-${end}/${file.size}`;

    const res = await axios.put(uploadUrl, chunk, {
      headers: {
        'Content-Type': contentType,
        'Content-Range': contentRange,
      },
      signal,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      validateStatus: statusAllowsGcsResumableChunkResponse,
    });

    if (res.status >= 200 && res.status < 300) {
      onProgress(100);
      return;
    }

    if (res.status !== 308) {
      throw new Error(
        `Unexpected upload status ${res.status} at offset ${offset}`,
      );
    }

    const rangeHeader = res.headers['range'] ?? res.headers['Range'];
    if (typeof rangeHeader === 'string') {
      const match = /^bytes=0-(\d+)$/.exec(rangeHeader.trim());
      if (match) {
        const confirmedEnd = parseInt(match[1], 10);
        offset = confirmedEnd + 1;
        onProgress(
          Math.min(99, Math.round((offset / file.size) * 100)),
        );
        continue;
      }
    }

    offset = end + 1;
    onProgress(Math.min(99, Math.round((offset / file.size) * 100)));
  }

  throw new Error('Resumable upload ended without success response');
}
